import { mcGetItem, mcSetItem, mcRemoveItem, isMcCloudSyncReady, getMcRemovedProfileIds } from './mcCloudSync';
import { activeSections, formatSectionLabel, normalizeSectionId } from './mcSections';
import { recordActivity } from './mcActivityEvents';

const MC_PROFILES_KEY = 'mc-profiles';
const MC_CURRENT_PROFILE_KEY = 'mc-current-profile';
const MC_CONTEXT_EMAIL_KEY = 'mc-current-user-email';
const MC_CURRENT_PROFILE_SESSION_KEY = 'matrixos-morpheus-active-profile-tab-v1';
const MC_CURRENT_PROFILE_SESSION_LEGACY_KEY = 'mc-current-profile-session';
const MC_PRIMARY_OWNER_EMAIL_KEY = 'mc-primary-owner-email';

const MC_ADMIN_STORE_VIEW_KEY = 'mc-admin-store-view';
const MC_PROFILE_EVENT = 'mc-profile-change';
const MC_ADMIN_STORE_VIEW_EVENT = 'mc-admin-store-view-change';

function safeParse(raw, fallback) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function slugToLabel(value = '') {
  return String(value || '')
    .replace(/^inventory_/i, '')
    .replace(/_Store$/i, '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function storeCompareKey(value = '') {
  return slugToLabel(value).toLowerCase().replace(/[^a-z0-9]/g, '');
}

function recordStoreValue(item = {}) {
  return item?.storeKey || item?.mapping?.systemSoh || item?.store || item?.storeName || item?.config?.storeName || '';
}

export function normalizeStoreValue(value = '') {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.toUpperCase() === 'ALL') return 'ALL';

  const withoutPrefix = raw.replace(/^inventory_/i, '');
  const cleaned = withoutPrefix
    .replace(/_Store$/i, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (!cleaned) return '';
  const titled = cleaned
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('_');

  return `inventory_${titled}`;
}

export function formatStoreLabel(value = '') {
  const normalized = normalizeStoreValue(value);
  if (!normalized || normalized.toLowerCase() === 'all' || normalized.toLowerCase() === 'inventory_all') return 'All Sites';
  return slugToLabel(normalized);
}

export function buildStoreOptions(headers = []) {
  const inventoryHeaders = (headers || []).filter((header) => /^inventory_/i.test(String(header || '')));
  const unique = Array.from(new Set(inventoryHeaders));
  return unique.length ? unique : [];
}


function compactProfileForSession(profile = null) {
  if (!profile) return null;
  const compact = {
    id: profile.id || '',
    email: profile.email || profile.userEmail || '',
    name: profile.name || '',
    surname: profile.surname || '',
    role: profile.role || '',
    accessLevel: profile.accessLevel || '',
    operationalRole: profile.operationalRole || '',
    store: profile.store || profile.storeKey || '',
    storeKey: profile.storeKey || profile.store || '',
    allowedSiteIds: Array.isArray(profile.allowedSiteIds) ? profile.allowedSiteIds : undefined,
    allowedSites: Array.isArray(profile.allowedSites) ? profile.allowedSites : undefined,
    siteIds: Array.isArray(profile.siteIds) ? profile.siteIds : undefined,
    assignedSectionIds: Array.isArray(profile.assignedSectionIds) ? profile.assignedSectionIds : undefined,
    assignedSections: Array.isArray(profile.assignedSections) ? profile.assignedSections : undefined,
    sectionIds: Array.isArray(profile.sectionIds) ? profile.sectionIds : undefined,
    tabs: Array.isArray(profile.tabs) ? profile.tabs : undefined,
    permissions: profile.permissions && typeof profile.permissions === 'object' ? profile.permissions : undefined,
    canFinalizeCount: profile.canFinalizeCount === true ? true : undefined,
    isPrimaryOwner: profile.isPrimaryOwner === true ? true : undefined,
    primaryOwner: profile.primaryOwner === true ? true : undefined,
  };
  Object.keys(compact).forEach((key) => {
    if (compact[key] === undefined || compact[key] === null || compact[key] === '') delete compact[key];
  });
  return compact;
}

function resolveStoredProfile(stored = null) {
  if (!stored || typeof stored !== 'object') return null;
  const profiles = loadProfiles();
  const match = profiles.find((item) => String(item?.id || '') === String(stored.id || ''));
  return match ? { ...stored, ...match } : stored;
}

function normalizeEmail(value = '') {
  return String(value || '').trim().toLowerCase();
}

export function adminIdForEmail(email = '') {
  const clean = normalizeEmail(email);
  if (!clean) return 'mc-admin';
  return `mc-admin-${clean.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 80)}`;
}

function readContextEmail() {
  if (typeof window === 'undefined') return '';
  return normalizeEmail(mcGetItem(MC_CONTEXT_EMAIL_KEY) || '');
}

export function setMcUserContext(email = '') {
  if (typeof window === 'undefined') return;
  const clean = normalizeEmail(email);
  if (clean) mcSetItem(MC_CONTEXT_EMAIL_KEY, clean);
}

function isAdminForCurrentEmail(profile, email = readContextEmail()) {
  if (!isAdminProfile(profile)) return false;
  const cleanEmail = normalizeEmail(email);
  const profileEmail = normalizeEmail(profile?.email || profile?.userEmail || '');
  const profileId = String(profile?.id || '').trim().toLowerCase();
  if (profileEmail && cleanEmail) return profileEmail === cleanEmail;
  // Only recognised admin identities may fall back to the current login.
  // A normal profile promoted to `role: admin` without an email must never
  // replace the signed-in organisation owner.
  if (!profileEmail && cleanEmail) {
    return profileId === 'mc-admin' || profileId === adminIdForEmail(cleanEmail).toLowerCase();
  }
  return false;
}


export function buildDefaultAdminProfile(email = '', displayName = '') {
  const cleanEmail = normalizeEmail(email || readContextEmail());
  const cleanDisplayName = String(displayName || '').trim();
  const nameFromEmail = cleanEmail ? cleanEmail.split('@')[0].replace(/[._-]+/g, ' ') : 'Admin';
  return {
    id: adminIdForEmail(cleanEmail),
    email: cleanEmail,
    name: cleanDisplayName || (cleanEmail ? nameFromEmail.replace(/\b\w/g, (m) => m.toUpperCase()) : 'Admin'),
    surname: '',
    pin: '000000',
    store: 'ALL',
    role: 'admin',
    accessLevel: 'owner',
    operationalRole: 'Owner',
    allowedSiteIds: ['ALL'],
    assignedSectionIds: ['ALL'],
    tabs: ['workspace','imports','prepared-data','sessions','archive','responses','notifications','sections','sites','tickets','profiles','billing','config','mapping','categories'],
    isPrimaryOwner: true,
  };
}

export function normalizeProfiles(profiles = [], email = '', displayName = '') {
  const cleanEmail = normalizeEmail(email || readContextEmail());
  const removed = getMcRemovedProfileIds();
  const list = Array.isArray(profiles) ? profiles.filter((item) => item && !removed.has(item.id)) : [];
  const isCanonicalAdminId = (item) => {
    const id = String(item?.id || '').toLowerCase();
    return id === 'mc-admin' || id.startsWith('mc-admin-');
  };

  // Primary ownership is identity data, not an access-level side effect. Only the
  // canonical admin identity may carry the protected flag. Promoting another
  // person to Administrator/Owner must never replace this record.
  const flagged = list.filter((item) => item?.isPrimaryOwner === true || item?.primaryOwner === true);
  const canonicalFlagged = flagged.find(isCanonicalAdminId) || flagged[0] || null;
  // Super Admin is the authority for the protected owner email. The synced
  // mc-primary-owner-email value is deliberately separate from editable
  // Directory contact fields, so changing a person's contact email can never
  // transfer or downgrade organisation ownership.
  const authoritativePrimaryEmail = normalizeEmail(mcGetItem(MC_PRIMARY_OWNER_EMAIL_KEY) || '');
  const storedPrimaryEmail = normalizeEmail(canonicalFlagged?.email || canonicalFlagged?.userEmail || '');
  const primaryEmail = authoritativePrimaryEmail || storedPrimaryEmail || cleanEmail;
  const canonicalPrimaryId = adminIdForEmail(primaryEmail);

  const primarySource = list.find((item) => String(item?.id || '') === canonicalPrimaryId)
    || list.find((item) => isCanonicalAdminId(item) && normalizeEmail(item?.email || item?.userEmail || '') === primaryEmail)
    || canonicalFlagged;

  const normalizeIdentityName = (item) => String([item?.name, item?.surname].filter(Boolean).join(' ')).trim().toLowerCase().replace(/\s+/g, ' ');
  const primaryDisplayName = String((cleanEmail === primaryEmail ? displayName : '') || primarySource?.name || '').trim();
  const primaryIdentityName = String([primaryDisplayName, primarySource?.surname].filter(Boolean).join(' ')).trim().toLowerCase().replace(/\s+/g, ' ');
  const duplicatePrimaryIdentity = list.find((item) => {
    if (!primaryIdentityName || String(item?.id || '') === canonicalPrimaryId) return false;
    const sameName = normalizeIdentityName(item) === primaryIdentityName;
    const itemEmail = normalizeEmail(item?.email || item?.userEmail || '');
    return sameName && itemEmail !== primaryEmail;
  }) || null;

  const primary = {
    ...buildDefaultAdminProfile(primaryEmail, cleanEmail === primaryEmail ? displayName : ''),
    ...(primarySource || {}),
    id: canonicalPrimaryId,
    email: primaryEmail,
    name: String((cleanEmail === primaryEmail ? displayName : '') || primarySource?.name || buildDefaultAdminProfile(primaryEmail).name || 'Owner').trim(),
    surname: primarySource?.surname || duplicatePrimaryIdentity?.surname || '',
    whatsapp: primarySource?.whatsapp || duplicatePrimaryIdentity?.whatsapp || '',
    pin: primarySource?.pin || duplicatePrimaryIdentity?.pin || '000000',
    role: 'admin',
    accessLevel: 'owner',
    operationalRole: 'Owner',
    isPrimaryOwner: true,
    primaryOwner: true,
    store: 'ALL',
    allowedSiteIds: ['ALL'],
    allowedSites: ['ALL'],
    siteIds: ['ALL'],
    assignedSectionIds: ['ALL'],
    assignedSections: ['ALL'],
    sectionIds: ['ALL'],
    tabs: ['workspace','imports','prepared-data','sessions','archive','responses','notifications','sections','sites','tickets','profiles','billing','config','mapping','categories'],
  };

  const seenIds = new Set([String(primary.id)]);
  const seenEmails = new Set(primaryEmail ? [primaryEmail] : []);
  const normalized = [primary];

  // Every authenticated administrator is a first-class Directory identity.
  // The Primary Owner remains protected, but another Super Admin login must
  // still receive its own selectable PIN profile instead of being collapsed
  // into the owner record.
  if (cleanEmail && cleanEmail !== primaryEmail && !removed.has(adminIdForEmail(cleanEmail)) && !list.some((item) => normalizeEmail(item?.email || item?.userEmail || '') === cleanEmail)) {
    const loginAdmin = buildDefaultAdminProfile(cleanEmail, displayName);
    normalized.push({
      ...loginAdmin,
      id: adminIdForEmail(cleanEmail),
      email: cleanEmail,
      name: String(displayName || loginAdmin.name || 'Administrator').trim(),
      surname: '',
      pin: '000000',
      role: 'admin',
      accessLevel: 'administrator',
      operationalRole: 'Administrator',
      isPrimaryOwner: false,
      primaryOwner: false,
      loginAccessStatus: 'active',
      loginInviteRequired: false,
      store: 'ALL',
      allowedSiteIds: ['ALL'],
      allowedSites: ['ALL'],
      siteIds: ['ALL'],
      assignedSectionIds: ['ALL'],
      assignedSections: ['ALL'],
      sectionIds: ['ALL'],
    });
    seenIds.add(String(loginAdmin.id));
    seenEmails.add(cleanEmail);
  }

  list.forEach((item) => {
    const id = String(item?.id || '');
    const itemEmail = normalizeEmail(item?.email || item?.userEmail || '');
    if (id === primary.id) return;
    if (duplicatePrimaryIdentity && id === String(duplicatePrimaryIdentity.id || '')) return;
    // Remove stale duplicate owner identities that copied the Primary Owner email.
    if (itemEmail && itemEmail === primaryEmail && (isCanonicalAdminId(item) || item?.isPrimaryOwner === true || item?.primaryOwner === true)) return;
    if (seenIds.has(id)) return;
    if (itemEmail && seenEmails.has(itemEmail)) return;

    const rawRole = String(item.role || '').trim().toLowerCase();
    const accessLevel = item.accessLevel || (['admin', 'owner', 'administrator'].includes(rawRole) ? (rawRole === 'owner' ? 'owner' : 'administrator') : 'standard');
    const operationalRole = item.operationalRole || (accessLevel === 'owner' ? 'Owner' : 'Stock Controller');
    const isAdministrator = accessLevel === 'administrator' || accessLevel === 'owner';
    const standardDefaults = ['workspace','imports','prepared-data','sessions','archive'];
    const administratorDefaults = ['workspace','imports','prepared-data','sessions','archive','responses','notifications','sections','sites','tickets'];
    const storedTabs = Array.isArray(item.tabs) ? item.tabs.filter(Boolean) : [];
    const stillHasStandardDefaults = storedTabs.length === standardDefaults.length && standardDefaults.every((key) => storedTabs.includes(key));
    const tabs = isAdministrator
      ? ((!storedTabs.length || stillHasStandardDefaults) ? administratorDefaults : storedTabs)
      : (storedTabs.length ? storedTabs : standardDefaults);
    const assignedSections = isAdministrator
      ? ['ALL']
      : (Array.isArray(item.assignedSectionIds) ? item.assignedSectionIds : item.assignedSections);

    normalized.push({
      ...item,
      email: itemEmail || item.email || '',
      role: isAdministrator ? 'admin' : 'user',
      accessLevel,
      operationalRole,
      tabs,
      isPrimaryOwner: false,
      primaryOwner: false,
      assignedSectionIds: assignedSections,
      assignedSections,
      sectionIds: assignedSections,
    });
    seenIds.add(id);
    if (itemEmail) seenEmails.add(itemEmail);
  });

  return normalized.sort((a, b) => {
    if (a.isPrimaryOwner === true && b.isPrimaryOwner !== true) return -1;
    if (b.isPrimaryOwner === true && a.isPrimaryOwner !== true) return 1;
    const diff = profileAccessRank(a?.accessLevel) - profileAccessRank(b?.accessLevel);
    if (diff) return diff;
    return buildProfileDisplayName(a).localeCompare(buildProfileDisplayName(b));
  });
}

function profileAccessRank(level = '') {
  const key = String(level || '').toLowerCase();
  if (key === 'owner') return 0;
  if (key === 'administrator') return 1;
  if (key === 'standard') return 2;
  return 3;
}

export function ensureAdminProfile(email = '', displayName = '') {
  // Pure read/normalize only. Do not write to storage from this function.
  // MorpheusProfileGate calls this while rendering/effects; writing here dispatches
  // storage events and causes React warnings: setState during another component render.
  const cleanEmail = normalizeEmail(email || readContextEmail());
  const raw = loadProfiles();
  return normalizeProfiles(raw, cleanEmail, displayName);
}

export function loadProfiles() {
  if (typeof window === 'undefined') return [];
  const parsed = safeParse(mcGetItem(MC_PROFILES_KEY), []);
  const removed = getMcRemovedProfileIds();
  const visible = (items) => items.filter((item) => item && !removed.has(item.id));
  if (Array.isArray(parsed)) return visible(parsed);
  // Firestore sometimes stores profiles as { profiles: [...] } instead of a raw array.
  // A fresh browser must accept both shapes before showing the Access Layer.
  if (Array.isArray(parsed?.profiles)) return visible(parsed.profiles);
  if (Array.isArray(parsed?.items)) return visible(parsed.items);
  if (Array.isArray(parsed?.value)) return visible(parsed.value);
  return [];
}

export function saveProfiles(profiles = []) {
  if (typeof window === 'undefined') return;
  const cleanEmail = readContextEmail();
  const next = normalizeProfiles(profiles, cleanEmail).map((item) => {
    if (item?.isPrimaryOwner === true || item?.primaryOwner === true) {
      return { ...item, role: 'admin', accessLevel: 'owner', operationalRole: 'Owner', store: 'ALL', allowedSiteIds: ['ALL'], assignedSectionIds: ['ALL'], tabs: ['workspace','imports','prepared-data','sessions','archive','responses','notifications','sections','sites','tickets','profiles','billing','config','mapping','categories'], isPrimaryOwner: true };
    }
    const rawRole = String(item.role || '').trim().toLowerCase();
    const accessLevel = item.accessLevel || (['admin', 'owner', 'administrator'].includes(rawRole) ? (rawRole === 'owner' ? 'owner' : 'administrator') : 'standard');
    return { ...item, accessLevel, role: accessLevel === 'owner' || accessLevel === 'administrator' ? 'admin' : 'user', isPrimaryOwner: false };
  });
  const previous = loadProfiles();
  mcSetItem(MC_PROFILES_KEY, JSON.stringify(next));
  const beforeById = new Map(previous.map((item) => [item?.id, item]));
  next.forEach((item) => {
    const before = beforeById.get(item?.id);
    if (!before) { recordActivity({category:'People',action:'Person created',title:`Person created · ${[item?.name,item?.surname].filter(Boolean).join(' ') || 'Profile'}`,targetId:item?.id,targetName:[item?.name,item?.surname].filter(Boolean).join(' '),metadata:{accessLevel:item?.accessLevel,operationalRole:item?.operationalRole}}); return; }
    const fields=['name','surname','email','whatsapp','accessLevel','operationalRole','pin','canFinalizeCount'];
    const changed=fields.filter((key)=>JSON.stringify(before?.[key] ?? '')!==JSON.stringify(item?.[key] ?? ''));
    const sitesChanged=JSON.stringify(before?.allowedSiteIds||[])!==JSON.stringify(item?.allowedSiteIds||[]);
    const sectionsChanged=JSON.stringify(before?.assignedSectionIds||[])!==JSON.stringify(item?.assignedSectionIds||[]);
    if (changed.length || sitesChanged || sectionsChanged) recordActivity({category: changed.some((k)=>['accessLevel','pin'].includes(k))?'Access':'People',action:'Directory updated',title:`Profile updated · ${[item?.name,item?.surname].filter(Boolean).join(' ') || 'Profile'}`,targetId:item?.id,targetName:[item?.name,item?.surname].filter(Boolean).join(' '),metadata:{changedFields:[...changed,...(sitesChanged?['sites']:[]),...(sectionsChanged?['sections']:[])]}});
    beforeById.delete(item?.id);
  });
  beforeById.forEach((item)=>recordActivity({category:'People',action:'Person removed',title:`Person removed · ${[item?.name,item?.surname].filter(Boolean).join(' ') || 'Profile'}`,targetId:item?.id,targetName:[item?.name,item?.surname].filter(Boolean).join(' '),severity:'attention'}));
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(MC_PROFILE_EVENT, { detail: next }));
}

function readSessionProfileFallback() {
  if (typeof window === 'undefined') return null;
  try {
    const current = safeParse(window.sessionStorage?.getItem(MC_CURRENT_PROFILE_SESSION_KEY), null);
    if (current) return current;
    // Migration fallback for tabs opened before MC480. The legacy key starts
    // with `mc-`, so cloud cache reconciliation may purge it. Copy it into the
    // tab-local lease immediately when it is still available.
    const legacy = safeParse(window.sessionStorage?.getItem(MC_CURRENT_PROFILE_SESSION_LEGACY_KEY), null);
    if (legacy) {
      try { window.sessionStorage?.setItem(MC_CURRENT_PROFILE_SESSION_KEY, JSON.stringify(legacy)); } catch (_) {}
    }
    return legacy;
  } catch (_) {
    return null;
  }
}

function writeSessionProfileFallback(profile) {
  if (typeof window === 'undefined') return;
  try {
    if (profile) {
      window.sessionStorage?.setItem(MC_CURRENT_PROFILE_SESSION_KEY, JSON.stringify(profile));
      // Keep the old key during the transition for code from an already-open
      // bundle, but the new non-mc key is the durable tab authority.
      window.sessionStorage?.setItem(MC_CURRENT_PROFILE_SESSION_LEGACY_KEY, JSON.stringify(profile));
    } else {
      window.sessionStorage?.removeItem(MC_CURRENT_PROFILE_SESSION_KEY);
      window.sessionStorage?.removeItem(MC_CURRENT_PROFILE_SESSION_LEGACY_KEY);
    }
  } catch (_) {}
}

function profileBelongsToCurrentEmail(profile) {
  // Firebase Authentication proves organisation membership only. Morpheus
  // permissions come exclusively from the profile selected in the Access Layer.
  return Boolean(profile);
}

export function getCurrentProfile() {
  if (typeof window === 'undefined') return null;

  // The tab-local Access Layer selection is the primary authority. Resolve the
  // latest Directory record by profile id only so PIN/profile edits can refresh
  // safely without allowing the authenticated email to replace the selected
  // identity.
  const sessionStored = readSessionProfileFallback();
  const stored = safeParse(mcGetItem(MC_CURRENT_PROFILE_KEY), null);
  const selectedSnapshot = sessionStored || stored;
  if (!selectedSnapshot) return null;

  const profiles = loadProfiles();
  const selectedId = String(selectedSnapshot?.id || '').trim();
  if (getMcRemovedProfileIds().has(selectedId)) return null;
  const authoritative = selectedId
    ? profiles.find((item) => String(item?.id || '').trim() === selectedId)
    : null;

  const resolved = authoritative
    ? { ...selectedSnapshot, ...authoritative }
    : selectedSnapshot;

  if (!profileBelongsToCurrentEmail(resolved)) return null;

  const compact = compactProfileForSession(resolved);
  writeSessionProfileFallback(compact);
  return resolved;
}

export function setCurrentProfile(profile) {
  if (typeof window === 'undefined') return;
  const compact = compactProfileForSession(profile);
  writeSessionProfileFallback(compact);
  mcSetItem(MC_CURRENT_PROFILE_KEY, JSON.stringify(compact));
  window.dispatchEvent(new CustomEvent(MC_PROFILE_EVENT, { detail: compact }));
}

export function clearCurrentProfile() {
  if (typeof window === 'undefined') return;
  writeSessionProfileFallback(null);
  mcRemoveItem(MC_CURRENT_PROFILE_KEY);
  window.dispatchEvent(new CustomEvent(MC_PROFILE_EVENT, { detail: null }));
}

export function isAdminProfile(profile) {
  if (!profile) return false;
  const role = String(profile?.role || '').trim().toLowerCase();
  const id = String(profile?.id || '').trim().toLowerCase();

  // The admin profile can briefly be read from the compact local session copy
  // while the shared profile list is rehydrating. In that moment the role can
  // be missing/stale, which makes the chrome hide admin tabs until a refresh.
  // Treat the reserved admin ids as admin too so the UI does not flicker into
  // operator mode during background sync.
  const accessLevel = String(profile?.accessLevel || '').trim().toLowerCase();
  return role === 'admin'
    || role === 'owner'
    || role === 'administrator'
    || accessLevel === 'owner'
    || accessLevel === 'administrator'
    || profile?.isPrimaryOwner === true
    || profile?.primaryOwner === true
    || id === 'admin-profile'
    || id === 'mc-admin'
    || id.startsWith('mc-admin-');
}

export function getProfileAccessLevel(profile = {}) {
  if (profile?.isPrimaryOwner === true || profile?.primaryOwner === true) return 'owner';
  if (profile?.accessLevel) return String(profile.accessLevel).toLowerCase();
  return isAdminProfile(profile) ? 'owner' : 'standard';
}

export function getProfileOperationalRole(profile = {}) {
  return profile?.operationalRole || (getProfileAccessLevel(profile) === 'owner' ? 'Owner' : 'Stock Controller');
}

export function isOwnerProfile(profile) {
  return getProfileAccessLevel(profile) === 'owner';
}

export function profileCanSeeTab(profile, tabKey = '') {
  if (isOwnerProfile(profile)) return true;
  const tabs = Array.isArray(profile?.tabs) ? profile.tabs : [];
  if (tabs.length) return tabs.includes(tabKey);
  if (isAdminProfile(profile)) return ['workspace','imports','prepared-data','sessions','archive','responses','notifications','sections','sites','tickets'].includes(tabKey);
  return ['workspace','imports','prepared-data','sessions','archive'].includes(tabKey);
}

export function canManageProfiles(profile) {
  return isOwnerProfile(profile) || (isAdminProfile(profile) && profileCanSeeTab(profile, 'profiles'));
}

export function canAccessAdminTabs(profile) {
  return isOwnerProfile(profile) || isAdminProfile(profile);
}

export function canFinalizeCount(profile) {
  if (isOwnerProfile(profile)) return true;
  return profile?.canFinalizeCount === true || profile?.permissions?.canFinalizeCount === true;
}

export function getVisibleMcTabs(profile) {
  const tabs = [
    { key: 'workspace', label: 'Dashboard', to: '/morpheus-count/workspace' },
    { key: 'imports', label: 'Intake', to: '/morpheus-count/imports' },
    { key: 'prepared-data', label: 'Review', to: '/morpheus-count/prepared-data' },
    { key: 'sessions', label: 'Sessions', to: '/morpheus-count/sessions' },
    { key: 'archive', label: 'Archives', to: '/morpheus-count/archive' },
    { key: 'responses', label: 'Responses', to: '/morpheus-count/workspace#responses' },
    { key: 'notifications', label: 'Response Intelligence', to: '/morpheus-count/workspace#notifications' },
    { key: 'sections', label: 'Sections', to: '/morpheus-count/sections' },
    { key: 'sites', label: 'Sites', to: '/morpheus-count/sites' },
    { key: 'tickets', label: 'My Tickets', to: '/morpheus-count/workspace#tickets' },
    { key: 'profiles', label: 'People', to: '/morpheus-count/profiles#directory' },
    { key: 'billing', label: 'Billing', to: '/morpheus-count/workspace#billing' },
    { key: 'config', label: 'Config', to: '/morpheus-count/config' },
    { key: 'mapping', label: 'Mapping', to: '/morpheus-count/mapping' },
    { key: 'categories', label: 'Inventory Configuration', to: '/morpheus-count/categories' },
  ];
  return tabs.filter((tab) => profileCanSeeTab(profile, tab.key));
}

export function filterRecordsForProfile(records = [], profile) {
  if (!Array.isArray(records)) return [];
  if (!profile) return records;

  const view = getAdminStoreView();
  const viewKey = view && view !== 'ALL' ? storeCompareKey(view) : '';
  const recordMatchesKey = (item, key) => {
    if (!key) return true;
    const candidates = [
      item?.storeKey,
      item?.siteId,
      item?.storeName,
      item?.store,
      item?.siteName,
      item?.mapping?.systemSoh,
      item?.config?.storeName,
    ].map(storeCompareKey).filter(Boolean);
    return candidates.includes(key);
  };

  if (isAdminProfile(profile)) {
    if (!viewKey) return records;
    return records.filter((item) => recordMatchesKey(item, viewKey));
  }

  const allowedKeys = getProfileAllowedSiteIds(profile)
    .filter((site) => site !== 'ALL')
    .map(storeCompareKey)
    .filter(Boolean);
  if (!allowedKeys.length) return [];

  const effectiveKeys = viewKey
    ? allowedKeys.filter((key) => key === viewKey)
    : allowedKeys;
  if (!effectiveKeys.length) return [];

  const siteFiltered = records.filter((item) => effectiveKeys.some((key) => recordMatchesKey(item, key)));
  const allowedSectionIds = getProfileAllowedSectionIds(profile).map(normalizeSectionId).filter(Boolean);
  if (!allowedSectionIds.length) return [];
  const allowedSections = new Set(allowedSectionIds);
  return siteFiltered.filter((item) => {
    // Full-site counts are visible only when the operator has access to every
    // currently active section. Role alone must not decide Full Count access.
    if (item?.fullSite === true || String(item?.sectionScope || '').toLowerCase() === 'full-site') {
      return profileHasAllSectionAccess(profile, activeSections());
    }
    const rawSection = String(
      item?.sectionId || item?.sectionName || item?.meta?.sectionId || item?.meta?.sectionName ||
      item?.detailSnapshot?.meta?.sectionId || item?.detailSnapshot?.meta?.sectionName || ''
    ).trim();
    if (!rawSection) return false;
    const sectionId = normalizeSectionId(rawSection);
    return Boolean(sectionId && sectionId !== 'section' && allowedSections.has(sectionId));
  });
}

export function profileStoreMatches(profile, value) {
  if (!profile || isAdminProfile(profile)) return true;
  const target = storeCompareKey(value);
  return getProfileAllowedSiteIds(profile).some((site) => storeCompareKey(site) === target);
}

export function buildProfileDisplayName(profile) {
  return [profile?.name, profile?.surname].filter(Boolean).join(' ').trim() || 'Profile';
}


export function getAdminStoreView() {
  if (typeof window === 'undefined') return 'ALL';
  return mcGetItem(MC_ADMIN_STORE_VIEW_KEY) || 'ALL';
}

export function setAdminStoreView(value) {
  if (typeof window === 'undefined') return;
  const nextValue = value || 'ALL';
  mcSetItem(MC_ADMIN_STORE_VIEW_KEY, nextValue);
  window.dispatchEvent(new CustomEvent(MC_ADMIN_STORE_VIEW_EVENT, { detail: nextValue }));
}

export function getActiveStoreScope(profile = getCurrentProfile()) {
  const view = getAdminStoreView();
  if (isAdminProfile(profile)) return view;

  const allowed = getProfileAllowedSiteIds(profile).filter((site) => site !== 'ALL');
  if (!allowed.length) return 'ALL';

  const normalizedView = normalizeStoreValue(view);
  if (normalizedView && normalizedView !== 'ALL') {
    const viewLabel = formatStoreLabel(normalizedView).toLowerCase();
    const match = allowed.find((site) => formatStoreLabel(site).toLowerCase() === viewLabel);
    if (match) return match;
  }

  const normalized = normalizeStoreValue(profile?.store || allowed[0] || '');
  const profileMatch = allowed.find((site) => formatStoreLabel(site).toLowerCase() === formatStoreLabel(normalized).toLowerCase());
  return profileMatch || allowed[0] || 'ALL';
}

export function useAdminStoreView() {
  const React = require('react');
  const [storeView, setStoreView] = React.useState(() => getAdminStoreView());

  React.useEffect(() => {
    // Some legacy MC hydration paths dispatch `storage` / admin-store-view events
    // synchronously while a route (notably Archives) is still rendering. Updating
    // CountChrome immediately from that event triggers React's cross-component
    // render warning. Defer the React publication to the next task and coalesce
    // bursts so the shared selector remains responsive without mutating React
    // during another component's render phase.
    let cancelled = false;
    let timer = null;

    function flush() {
      timer = null;
      if (cancelled) return;
      const next = getAdminStoreView();
      setStoreView((previous) => previous === next ? previous : next);
    }

    function sync() {
      if (cancelled || timer !== null) return;
      timer = window.setTimeout(flush, 0);
    }

    window.addEventListener(MC_ADMIN_STORE_VIEW_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener(MC_ADMIN_STORE_VIEW_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return storeView;
}

export function useCurrentMcProfile() {
  const React = require('react');
  const [profile, setProfile] = React.useState(() => getCurrentProfile());

  React.useEffect(() => {
    // Profile/storage events can be dispatched synchronously by legacy hydration
    // helpers while another Morpheus route is in the middle of rendering. Calling
    // setState directly from that event creates React's cross-component render
    // warning (for example Archive rendering while CountChrome is updated).
    // Always cross a microtask boundary and coalesce bursts before publishing the
    // resolved profile to React. This keeps the profile event bus synchronous for
    // non-React consumers without allowing it to mutate React during render.
    let cancelled = false;
    let queued = false;

    function flush() {
      queued = false;
      if (cancelled) return;
      const next = getCurrentProfile();
      setProfile((previous) => {
        try {
          if (JSON.stringify(previous || null) === JSON.stringify(next || null)) return previous;
        } catch (_) {}
        return next;
      });
    }

    function sync() {
      if (cancelled || queued) return;
      queued = true;
      if (typeof queueMicrotask === 'function') queueMicrotask(flush);
      else Promise.resolve().then(flush);
    }

    window.addEventListener(MC_PROFILE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      cancelled = true;
      window.removeEventListener(MC_PROFILE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return profile;
}


export function useSharedMcProfiles() {
  const React = require('react');
  const [profiles, setProfiles] = React.useState(() => ensureAdminProfile());

  React.useEffect(() => {
    function sync() {
      setProfiles(ensureAdminProfile());
    }
    sync();
    window.addEventListener(MC_PROFILE_EVENT, sync);
    window.addEventListener('mc-cloud-sync', sync);
    window.addEventListener('mc-cloud-sync-ready', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(MC_PROFILE_EVENT, sync);
      window.removeEventListener('mc-cloud-sync', sync);
      window.removeEventListener('mc-cloud-sync-ready', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return profiles;
}


// Compatibility helpers used by MorpheusProfileGate and Profiles after siteId lock.
// Kept defensive because older MC screens can pass site options as arrays, maps, or strings.
function normalizeSiteOptionsList(siteOptions = []) {
  if (Array.isArray(siteOptions)) return siteOptions;
  if (siteOptions && typeof siteOptions === 'object') {
    if (Array.isArray(siteOptions.options)) return siteOptions.options;
    if (Array.isArray(siteOptions.sites)) return siteOptions.sites;
    return Object.values(siteOptions);
  }
  return [];
}

function optionSiteId(option) {
  if (typeof option === 'string') return normalizeStoreValue(option);
  return normalizeStoreValue(option?.siteId || option?.id || option?.value || option?.storeKey || option?.key || option?.csvKey || option?.name || '');
}

function optionSiteLabel(option) {
  if (typeof option === 'string') return formatStoreLabel(option);
  return option?.label || option?.name || option?.siteName || formatStoreLabel(optionSiteId(option));
}

export function getProfileAllowedSiteIds(profile = {}, siteOptions = []) {
  if (isAdminProfile(profile)) return ['ALL'];

  const rawAllowed = Array.isArray(profile?.allowedSiteIds)
    ? profile.allowedSiteIds
    : Array.isArray(profile?.allowedSites)
      ? profile.allowedSites
      : Array.isArray(profile?.siteIds)
        ? profile.siteIds
        : profile?.store
          ? [profile.store]
          : [];

  const allowedIds = rawAllowed
    .map((value) => normalizeStoreValue(value))
    .filter(Boolean);

  const activeIds = normalizeSiteOptionsList(siteOptions)
    .filter((option) => option?.active !== false && option?.disabled !== true)
    .map(optionSiteId)
    .filter(Boolean);

  if (!activeIds.length) return Array.from(new Set(allowedIds));
  return Array.from(new Set(allowedIds.filter((id) => activeIds.includes(id))));
}


function normalizeSectionOptionsList(sectionOptions = []) {
  if (Array.isArray(sectionOptions)) return sectionOptions;
  if (sectionOptions && typeof sectionOptions === 'object') {
    if (Array.isArray(sectionOptions.options)) return sectionOptions.options;
    if (Array.isArray(sectionOptions.sections)) return sectionOptions.sections;
    return Object.values(sectionOptions);
  }
  return [];
}

function optionSectionId(option) {
  if (typeof option === 'string') return normalizeSectionId(option);
  return normalizeSectionId(option?.sectionId || option?.id || option?.value || option?.key || option?.name || '');
}

function optionSectionLabel(option) {
  if (typeof option === 'string') return formatSectionLabel(option);
  return option?.label || option?.name || formatSectionLabel(optionSectionId(option));
}

export function getProfileAllowedSectionIds(profile = {}, sectionOptions = []) {
  if (isAdminProfile(profile)) return ['ALL'];

  const rawAllowed = Array.isArray(profile?.assignedSectionIds)
    ? profile.assignedSectionIds
    : Array.isArray(profile?.assignedSections)
      ? profile.assignedSections
      : Array.isArray(profile?.sectionIds)
        ? profile.sectionIds
        : Array.isArray(profile?.sections)
          ? profile.sections
          : [];

  const allowedIds = rawAllowed
    .map((value) => (typeof value === 'string' ? value : value?.sectionId || value?.id || value?.name || ''))
    .map((value) => normalizeSectionId(value))
    .filter(Boolean);

  const activeIds = normalizeSectionOptionsList(sectionOptions)
    .filter((option) => option?.active !== false && option?.disabled !== true)
    .map(optionSectionId)
    .filter(Boolean);

  if (!activeIds.length) return Array.from(new Set(allowedIds));
  return Array.from(new Set(allowedIds.filter((id) => activeIds.includes(id))));
}

export function profileHasAllSectionAccess(profile = {}, sectionOptions = []) {
  if (isAdminProfile(profile)) return true;

  const options = normalizeSectionOptionsList(sectionOptions);
  const effectiveOptions = options.length ? options : activeSections();
  const activeIds = effectiveOptions
    .filter((option) => option?.active !== false && option?.disabled !== true)
    .map(optionSectionId)
    .filter(Boolean);

  if (!activeIds.length) return false;
  const allowedIds = new Set(getProfileAllowedSectionIds(profile, effectiveOptions));
  return activeIds.every((id) => allowedIds.has(id));
}

export function formatProfileAllowedSections(profile = {}, sectionOptions = []) {
  if (isAdminProfile(profile)) return 'All Sections';

  const ids = getProfileAllowedSectionIds(profile, sectionOptions);
  if (!ids.length) return 'No sections assigned';

  const options = normalizeSectionOptionsList(sectionOptions);
  const labels = ids.map((id) => {
    const match = options.find((option) => optionSectionId(option) === id);
    return match ? optionSectionLabel(match) : formatSectionLabel(id);
  });

  return labels.join(', ');
}

export function profileSectionMatches(profile, value) {
  if (!profile || isAdminProfile(profile)) return true;
  const target = normalizeSectionId(value);
  return getProfileAllowedSectionIds(profile).some((sectionId) => normalizeSectionId(sectionId) === target);
}

export function formatProfileAllowedSites(profile = {}, siteOptions = []) {
  if (isAdminProfile(profile)) return 'All Sites';

  const ids = getProfileAllowedSiteIds(profile, siteOptions);
  if (!ids.length) return 'No active sites';

  const options = normalizeSiteOptionsList(siteOptions);
  const labels = ids.map((id) => {
    const match = options.find((option) => optionSiteId(option) === id);
    return match ? optionSiteLabel(match) : formatStoreLabel(id);
  });

  return labels.join(', ');
}
